/**
 * EL REPORTE QUE EL CLIENTE MANDÓ COMO TEXTO, PARTIDO EN PREGUNTAS (20-10).
 *
 * Los que mandaban el mensual por WhatsApp o por correo pegaban la plantilla de Jesús
 * rellena: «1. Peso actual (en ayunas...): 88  2. ¿Cómo has llevado la dieta este mes?
 * (Bien / Regular / Mal): regular ... 12. ¿Hay algo más que quieras añadir?: MEDIDAS:
 * Hombros: 122 ...». Llega en `notes` del reporte o en `comentarioCliente` de Calma, y la
 * ficha lo pintaba de corrido: Francisco, «aquí no se entiende».
 *
 * Aquí se parte en {pregunta corta, respuesta}, se sacan las medidas con su nombre y se
 * quitan las preguntas sin contestar y las frases de la plantilla. Si el texto no es la
 * plantilla (menos de dos preguntas numeradas seguidas), devuelve `null` y se pinta tal cual.
 *
 * NO SE PARTE POR CUALQUIER NÚMERO: solo por el siguiente que toca (1, luego 2...) y al
 * principio de una línea, o en medio si detrás viene la pregunta («¿»). Así «4 pastillas»
 * o «22.00 horas» dentro de una respuesta no abren una pregunta nueva.
 */
import { MEDIDAS } from './medidas';

// Lo que no es texto: la flecha 👉 de la plantilla, los iconos que pegó algún teléfono como
// caracteres privados (U+100000 en adelante) y el «> » de un correo citado.
const limpiar = (t) => String(t)
    .replace(/\r\n?/g, '\n')
    .replace(/👉/gu, ' ')
    .replace(/[\u{F0000}-\u{10FFFF}]/gu, ' ')
    .replace(/^[ \t]*>[ \t]?/gm, '')
    .replace(/\t/g, ' ');

const sinTildes = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Dónde empieza la pregunta `n` a partir de `desde`: [inicio del número, inicio de la
// pregunta] o null. `saltada`: si es una que viene después de alguna que el cliente borró,
// solo vale a principio de línea y con la pregunta detrás («¿» o mayúscula).
const buscarPregunta = (texto, n, desde, saltada = false) => {
    const re = saltada
        ? new RegExp(`(^|\\n)[ ]*${n}\\.[ ]+(?=[¿A-ZÁÉÍÓÚ])`, 'g')
        : new RegExp(`(^|\\n)[ ]*${n}\\.[ ]+|[ ]${n}\\.[ ]+(?=¿)`, 'g');
    re.lastIndex = desde;
    const m = re.exec(texto);
    return m ? [m.index + (m[1] || '').length, m.index + m[0].length] : null;
};

// La siguiente pregunta: la que toca o, si el cliente borró alguna, hasta tres más allá; la
// que aparezca antes en el texto.
const siguientePregunta = (texto, n, desde) => {
    let mejor = null;
    for (let k = n; k <= n + 3; k++) {
        const m = buscarPregunta(texto, k, desde, k > n);
        if (m && (!mejor || m[0] < mejor.numero)) mejor = { n: k, numero: m[0], pregunta: m[1] };
    }
    return mejor;
};

// Dónde acaba el enunciado: en el «):» o «?:» de la plantilla; si no, en los primeros dos
// puntos; si no, en el salto de línea.
const partirEnunciado = (trozo) => {
    const fin = /[)?][ ]*:/.exec(trozo);
    if (fin) return [trozo.slice(0, fin.index + 1), trozo.slice(fin.index + fin[0].length)];
    const dosPuntos = trozo.indexOf(':');
    if (dosPuntos >= 0) return [trozo.slice(0, dosPuntos), trozo.slice(dosPuntos + 1)];
    const salto = trozo.indexOf('\n');
    return salto >= 0 ? [trozo.slice(0, salto), trozo.slice(salto + 1)] : [trozo, ''];
};

// La pregunta corta: sin los paréntesis de ayuda de la plantilla.
const preguntaCorta = (enunciado) => enunciado
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[\s:]+$/, '')
    .trim();

// Las frases de la plantilla que no son respuesta (detrás de la pregunta 12).
const DE_LA_PLANTILLA = [
    'ademas de esto, adjuntame', 'posicion relajada', 'recuerda hacerlas siempre',
    'mismas condiciones para poder comparar', 'aqui tienes el video de como tomarte las medidas',
    'https://vimeo.com/', 'medidas:',
];
const esDeLaPlantilla = (linea) => {
    const l = sinTildes(linea).trim();
    return DE_LA_PLANTILLA.some(p => l.startsWith(p)) || l === 'medidas';
};

// «Hombros: 122», «Brazo derecho relajado:36», «Cintura: 100 cm». El nombre tiene que ser
// el de una de las diez (con «relajado» o sin él) y el número, lo único de la línea.
const NOMBRES = MEDIDAS.map(m => ({ key: m.key, label: m.label, nombre: sinTildes(m.label).replace(/ relajado$/, '') }));
const leerMedida = (linea) => {
    const m = /^\s*([a-zA-ZÁÉÍÓÚáéíóúñÑ ]+?)\s*:?\s*(\d{1,3}(?:[.,]\d+)?)\s*(cm)?\s*\.?\s*$/.exec(linea);
    if (!m) return null;
    const nombre = sinTildes(m[1]).replace(/ relajado$/, '').trim();
    const medida = NOMBRES.find(x => x.nombre === nombre);
    return medida ? { key: medida.key, label: medida.label, valor: Number(m[2].replace(',', '.')) } : null;
};

// Las líneas cortadas a mitad de frase (el texto pegado de un PDF) se vuelven a unir: si la
// de antes no acaba en punto y la siguiente empieza en minúscula, es la misma frase.
const unirLineas = (hecho, linea) => {
    if (!hecho) return linea;
    const sigue = !/[.!?:;)]$/.test(hecho) && /^[a-záéíóúñü]/.test(linea);
    return `${hecho}${sigue ? ' ' : '\n'}${linea}`;
};

const lineas = (t) => t.split('\n').map(l => l.replace(/[ ]+/g, ' ').trim()).filter(Boolean);

/**
 * @returns {null | { intro: string|null, preguntas: {n: number, pregunta: string, respuesta: string}[],
 *                    medidas: {key: string, label: string, valor: number}[] }}
 */
export const partirElReporte = (texto) => {
    if (!texto || typeof texto !== 'string') return null;
    const t = limpiar(texto);
    // La primera tiene que ser la 1: un «3. algo» suelto no hace plantilla.
    const primera = buscarPregunta(t, 1, 0);
    const marcas = [];
    let marca = primera && { n: 1, numero: primera[0], pregunta: primera[1] };
    while (marca && marcas.length < 40) {
        marcas.push(marca);
        marca = siguientePregunta(t, marca.n + 1, marca.pregunta);
    }
    if (marcas.length < 2) return null;

    const medidas = [];
    const preguntas = marcas.map((m, i) => {
        const trozo = t.slice(m.pregunta, i + 1 < marcas.length ? marcas[i + 1].numero : t.length);
        const [enunciado, resto] = partirEnunciado(trozo);
        const respuesta = lineas(resto).filter((l) => {
            const medida = leerMedida(l);
            if (medida) {
                if (!medidas.some(x => x.key === medida.key)) medidas.push(medida);
                return false;
            }
            return !esDeLaPlantilla(l);
        }).reduce(unirLineas, '');
        return { n: m.n, pregunta: preguntaCorta(enunciado), respuesta };
    }).filter(p => p.respuesta);

    const intro = lineas(t.slice(0, marcas[0].numero)).join('\n') || null;
    return { intro, preguntas, medidas };
};
